import { FormEvent, useState } from 'react';
import { Link } from 'react-router-dom';
import { useScrollAnimation } from '../../hooks/useScrollAnimation';
import SEOHead from '@/components/feature/SEOHead';

const benefits = [
  { icon: 'ri-home-heart-line', title: 'Comfort of Home', desc: 'Learn in a familiar environment that supports focus and reduces anxiety.' },
  { icon: 'ri-tools-line', title: 'Fully Equipped', desc: 'We bring all necessary equipment directly to you — no extra setup required.' },
  { icon: 'ri-user-star-line', title: 'Personalized Focus', desc: 'One-on-one attention tailored specifically to your learning style and pace.' },
  { icon: 'ri-car-line', title: 'No Transportation Needed', desc: 'Perfect for families with transportation limitations or scheduling challenges.' },
];

const inHomeJsonLd = {
  '@context': 'https://schema.org',
  '@type': 'Service',
  name: 'In-Home Creative Development Programs',
  url: `${import.meta.env.VITE_SITE_URL || 'https://example.com'}/in-home-programs`,
  description: 'Creative Institute LLC offers in-home music and arts sessions for individuals who benefit from learning in a comfortable and familiar environment across North Carolina.',
  provider: {
    '@type': 'EducationalOrganization',
    name: 'Creative Institute LLC',
    telephone: '910-242-4567',
  },
  areaServed: { '@type': 'State', name: 'North Carolina' },
  serviceType: 'In-Home Music & Arts Education',
};

function HeroSection() {
  return (
    <section className="relative min-h-[70vh] flex items-center overflow-hidden">
      <div className="absolute inset-0 z-0">
        <img
          src="https://public.readdy.ai/ai/img_res/f3989662300f609b7a8f20f7b434c2a9.jpg"
          alt="In-Home Creative Development"
          className="w-full h-full object-cover object-top"
        />
        <div className="absolute inset-0 bg-gradient-to-r from-[#1E3A8A]/90 via-[#2D1B69]/80 to-[#5B21B6]/60" />
      </div>
      <div className="absolute inset-0 pointer-events-none select-none z-[1]">
        <img
          src="https://static.readdy.ai/image/b0ab6c1eb002a8cf89683099ba8ee0be/a9a19e6966780984f72576a4f2fc1316.png"
          alt=""
          className="absolute top-[-30px] right-[-10px] w-[560px] h-auto object-contain opacity-[0.15] mix-blend-screen"
        />
      </div>
      <div className="relative z-10 max-w-screen-xl mx-auto px-8 py-32 pt-44">
        <span className="hero-badge inline-block px-4 py-1.5 rounded-full border border-brand-yellow/50 text-brand-yellow font-body text-xs uppercase tracking-widest bg-brand-yellow/10 mb-6">
          In-Home Creative Development
        </span>
        <h1 className="font-heading text-white text-5xl lg:text-7xl font-black leading-tight mb-6 max-w-4xl">
          We Come to You.<br />
          <span className="text-brand-yellow">Excellence, Delivered.</span>
        </h1>
        <p className="font-body text-white/80 text-lg lg:text-xl max-w-2xl leading-relaxed mb-8">
          Creative Institute offers in-home sessions for individuals who benefit from learning in a more comfortable and familiar environment — meeting you exactly where you are.
        </p>
        <div className="flex flex-wrap gap-4">
          <Link to="/pricing-support" className="whitespace-nowrap inline-flex items-center gap-2 px-8 py-4 rounded-full bg-brand-yellow text-brand-blue-dark font-body font-bold text-sm cursor-pointer hover:bg-brand-yellow-light transition-colors">
            <i className="ri-arrow-right-circle-line" /> View Plans &amp; Pricing
          </Link>
          <Link to="/contact" className="whitespace-nowrap inline-flex items-center gap-2 px-8 py-4 rounded-full border-2 border-white/60 text-white font-body font-semibold text-sm cursor-pointer hover:border-white hover:bg-white/10 transition-colors">
            <i className="ri-phone-line" /> Ask a Question
          </Link>
        </div>
      </div>
    </section>
  );
}

function AboutSection() {
  const { ref, isVisible } = useScrollAnimation({ threshold: 0.1 });
  return (
    <section ref={ref} className="py-24 bg-gradient-to-b from-[#F0F4FF] to-white relative overflow-hidden">
      <div className="absolute top-0 right-0 w-80 h-80 bg-[#EEF2FF] rounded-full blur-3xl opacity-50 pointer-events-none" />
      <div className="max-w-screen-xl mx-auto px-8">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center">
          <div className={`scroll-hidden-tilt-left ${isVisible ? 'scroll-visible' : ''}`}>
            <span className="inline-block px-4 py-1.5 rounded-full bg-brand-yellow/15 text-brand-yellow-dark font-body text-xs uppercase tracking-widest font-semibold border border-brand-yellow/30 mb-5">
              Our Goal
            </span>
            <h2 className="font-heading text-4xl lg:text-5xl font-black text-brand-blue-dark mb-6">
              Meeting Every Individual<br />
              <span className="text-transparent bg-clip-text bg-gradient-to-r from-[#1E3A8A] to-[#5B21B6]">Where They Are</span>
            </h2>
            <p className="font-body text-gray-600 text-base leading-loose mb-4">
              Our goal is to meet each individual where they are, creating a supportive space that allows for confidence, focus, and growth. This option is ideal for:
            </p>
            <ul className="space-y-3 mb-6">
              {[
                'Neurodivergent individuals who thrive in familiar settings',
                'Beginners who prefer a personalized, low-pressure environment',
                'Families with transportation limitations',
                'Individuals who prefer to learn in the comfort of their own home',
              ].map((item, i) => (
                <li
                  key={item}
                  className={`scroll-hidden-blur ${isVisible ? 'scroll-visible' : ''} flex items-start gap-3 font-body text-gray-600 text-sm`}
                  style={{ transitionDelay: `${i * 80 + 200}ms` }}
                >
                  <span className="w-5 h-5 flex items-center justify-center rounded-full bg-brand-yellow/20 text-brand-yellow-dark flex-shrink-0 mt-0.5">
                    <i className="ri-check-line text-xs" />
                  </span>
                  {item}
                </li>
              ))}
            </ul>
            <p className="font-body text-gray-500 text-sm leading-relaxed italic">
              Availability is based on location and scheduling. Travel fees may apply depending on distance.
            </p>
          </div>
          <div className="grid grid-cols-2 gap-5">
            {benefits.map((b, i) => (
              <div
                key={b.title}
                className={`scroll-hidden-blur ${isVisible ? 'scroll-visible' : ''} p-6 rounded-2xl bg-gradient-to-br from-white to-[#F8F7FF] border border-[#EDE9FE] card-lift`}
                style={{ transitionDelay: `${i * 120 + 100}ms` }}
              >
                <div className="w-11 h-11 flex items-center justify-center rounded-xl bg-gradient-to-br from-[#1E3A8A] to-[#5B21B6] text-white mb-4 wiggle-hover">
                  <i className={`${b.icon} text-xl`} />
                </div>
                <h4 className="font-heading text-brand-blue-dark text-sm font-bold mb-2">{b.title}</h4>
                <p className="font-body text-gray-500 text-xs leading-relaxed">{b.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
}

function EquipmentBanner() {
  const { ref, isVisible } = useScrollAnimation({ threshold: 0.2 });
  return (
    <section ref={ref} className="py-16 bg-gradient-to-r from-[#FEF9C3] via-[#FFFBEB] to-[#FEF3C7]">
      <div className={`scroll-hidden-scale-blur ${isVisible ? 'scroll-visible' : ''} max-w-4xl mx-auto px-8 text-center`}>
        <div className="w-16 h-16 flex items-center justify-center rounded-2xl bg-brand-yellow mx-auto mb-5 wiggle-hover glow-yellow">
          <i className="ri-suitcase-2-line text-3xl text-brand-blue-dark" />
        </div>
        <h3 className="font-heading text-brand-blue-dark text-3xl font-black mb-4">Fully Equipped Experience</h3>
        <p className="font-body text-gray-700 text-base leading-relaxed">
          For your convenience, we bring all necessary equipment directly to you — ensuring a complete and high-quality learning experience without the need for additional setup or resources.
        </p>
      </div>
    </section>
  );
}

function InquiryFormSection() {
  const { ref, isVisible } = useScrollAnimation({ threshold: 0.1 });
  const [status, setStatus] = useState<'idle' | 'submitting' | 'success' | 'error'>('idle');
  const [charCount, setCharCount] = useState(0);

  const handleSubmit = async (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const form = e.currentTarget;
    const msg = (form.elements.namedItem('message') as HTMLTextAreaElement)?.value || '';
    if (msg.length > 500) return;
    setStatus('submitting');
    try {
      const formData = new URLSearchParams();
      const elements = form.elements;
      for (let i = 0; i < elements.length; i++) {
        const el = elements[i] as HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement;
        if (el.name && el.value && el.type !== 'submit') {
          formData.append(el.name, el.value);
        }
      }
      const res = await fetch('https://readdy.ai/api/form/d72253k991ove783t9c0', {
        method: 'POST',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        body: formData.toString(),
      });
      setStatus(res.ok ? 'success' : 'error');
      if (res.ok) { form.reset(); setCharCount(0); }
    } catch { setStatus('error'); }
  };

  return (
    <section ref={ref} className="py-24 bg-white">
      <div className="max-w-2xl mx-auto px-8">
        <div className={`scroll-hidden ${isVisible ? 'scroll-visible' : ''} text-center mb-12`}>
          <h2 className="font-heading text-4xl font-black text-brand-blue-dark mb-4">Ready to Get Started?</h2>
          <p className="font-body text-gray-500 text-base">Fill out the form below and we will reach out to schedule your first session.</p>
        </div>

        {status === 'success' ? (
          <div className={`scroll-hidden ${isVisible ? 'scroll-visible' : ''} text-center p-14 rounded-3xl bg-gradient-to-br from-[#EEF2FF] to-[#F5F3FF] border border-brand-purple/10`}>
            <div className="w-16 h-16 flex items-center justify-center rounded-full bg-brand-purple/15 text-brand-purple mx-auto mb-5">
              <i className="ri-check-double-line text-4xl" />
            </div>
            <h3 className="font-heading text-2xl font-black text-brand-blue-dark mb-3">Inquiry Received!</h3>
            <p className="font-body text-gray-500 text-sm max-w-xs mx-auto">We will be in touch within 24 hours to discuss your in-home program options.</p>
          </div>
        ) : (
          <form data-readdy-form onSubmit={handleSubmit} className={`scroll-hidden ${isVisible ? 'scroll-visible' : ''} space-y-5 bg-gradient-to-br from-[#F8F7FF] to-white p-8 rounded-3xl border border-[#EDE9FE]`}>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
              <div>
                <label className="font-body text-xs font-semibold text-gray-500 uppercase tracking-widest mb-1.5 block">Full Name *</label>
                <input name="name" required type="text" placeholder="Your full name" className="w-full px-4 py-3 rounded-xl border border-gray-200 font-body text-sm text-gray-700 focus:outline-none focus:border-brand-purple bg-white" />
              </div>
              <div>
                <label className="font-body text-xs font-semibold text-gray-500 uppercase tracking-widest mb-1.5 block">Email *</label>
                <input name="email" required type="email" placeholder="your@email.com" className="w-full px-4 py-3 rounded-xl border border-gray-200 font-body text-sm text-gray-700 focus:outline-none focus:border-brand-purple bg-white" />
              </div>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
              <div>
                <label className="font-body text-xs font-semibold text-gray-500 uppercase tracking-widest mb-1.5 block">Phone</label>
                <input name="phone" type="tel" placeholder="(910) 000-0000" className="w-full px-4 py-3 rounded-xl border border-gray-200 font-body text-sm text-gray-700 focus:outline-none focus:border-brand-purple bg-white" />
              </div>
              <div>
                <label className="font-body text-xs font-semibold text-gray-500 uppercase tracking-widest mb-1.5 block">Plan Interest</label>
                <select name="plan_interest" className="w-full px-4 py-3 rounded-xl border border-gray-200 font-body text-sm text-gray-700 focus:outline-none focus:border-brand-purple bg-white cursor-pointer">
                  <option value="">Select a plan</option>
                  <option value="Foundation Plan - $160/mo">Foundation Plan ($160/mo)</option>
                  <option value="Standard Plan - $180/mo">Standard Plan ($180/mo)</option>
                  <option value="Elite Development Plan">Elite Development Plan</option>
                  <option value="Not Sure Yet">Not sure yet</option>
                </select>
              </div>
            </div>
            <div>
              <label className="font-body text-xs font-semibold text-gray-500 uppercase tracking-widest mb-1.5 block">Area / City</label>
              <input name="location" type="text" placeholder="Your city or area (for scheduling)" className="w-full px-4 py-3 rounded-xl border border-gray-200 font-body text-sm text-gray-700 focus:outline-none focus:border-brand-purple bg-white" />
            </div>
            <div>
              <div className="flex items-center justify-between mb-1.5">
                <label className="font-body text-xs font-semibold text-gray-500 uppercase tracking-widest">Additional Notes</label>
                <span className={`font-body text-xs ${charCount > 500 ? 'text-red-500' : 'text-gray-400'}`}>{charCount}/500</span>
              </div>
              <textarea name="message" rows={4} maxLength={500} onChange={(e) => setCharCount(e.target.value.length)} placeholder="Tell us a bit about who you are and what you are looking for..." className="w-full px-4 py-3 rounded-xl border border-gray-200 font-body text-sm text-gray-700 focus:outline-none focus:border-brand-purple bg-white resize-none" />
            </div>
            {status === 'error' && <p className="font-body text-red-500 text-sm">Something went wrong. Please try again.</p>}
            <button type="submit" disabled={status === 'submitting' || charCount > 500} className="whitespace-nowrap w-full py-4 rounded-xl bg-gradient-to-r from-[#1E3A8A] to-[#5B21B6] text-white font-body font-bold text-base hover:opacity-90 transition-all cursor-pointer disabled:opacity-60">
              {status === 'submitting' ? 'Submitting...' : 'Submit Inquiry'}
            </button>
          </form>
        )}
      </div>
    </section>
  );
}

export default function InHomeProgramsPage() {
  return (
    <main>
      <SEOHead
        title="In-Home Music & Arts Programs | Creative Institute LLC NC"
        description="Creative Institute LLC brings professional music and arts instruction directly to your home across North Carolina. Fully equipped, personalized, and flexible scheduling. We come to you."
        keywords="in-home music lessons NC, mobile music instruction, home music lessons North Carolina, in-home arts education, Creative Institute in-home programs"
        canonical="/in-home-programs"
        jsonLd={inHomeJsonLd}
      />
      <HeroSection />
      <AboutSection />
      <EquipmentBanner />
      <InquiryFormSection />
    </main>
  );
}
